import React, { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Upload,
  Button,
  Table,
  Typography,
  Card,
  message,
  Modal,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  InboxOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { getAsignaturasPorCurso, getCursosPorPeriodo, getPeriodos } from '../api/api';

const { Title } = Typography;
const { Dragger } = Upload;

const MAX_MB = 2;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const inputStyle = {
  fontSize: '16px',
  height: 48,
  width: '100%', // asegura ancho completo
};

const labelStyle = {
  fontSize: '16px',
  fontWeight: 500,
};

const SubirRecurso = () => {

  const [modal, modalContextHolder] = Modal.useModal();

  const [form] = Form.useForm();
  const [archivos, setArchivos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [loading, setLoading] = useState(true); // Para manejar el estado de carga
  const [periodos, setPeriodos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [selectedPeriodo, setSelectedPeriodo] = useState(null);
  const [asignaturas, setAsignaturas] = useState([]);
  const [loadingAsignaturas, setLoadingAsignaturas] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState(null);
  const [selectedAsignatura, setSelectedAsignatura] = useState(null);
  const [subiendo, setSubiendo] = useState(false); // Nuevo estado

  useEffect(() => {
    const fetchPeriodos = async () => {
      const { success, status,  message: errorMessage, data } = await getPeriodos();
      if(success){
          setPeriodos(
          data.map((p) => ({
            value: p.id_periodo,  
            label: p.nombre_periodo,
          }))
        );
        setLoading(false);
      }else{
        if(status === 401){
          console.log(errorMessage);
        }
      }
    };

    fetchPeriodos();
  }, []);

  // Cargar los cursos cuando se selecciona un período
  useEffect(() => {
    if (selectedPeriodo) {
      setLoadingCursos(true); // Activar el loading de los cursos
      const fetchCursos = async () => {
        const cursosData = await getCursosPorPeriodo(selectedPeriodo);
        if (cursosData.success !== false) {
          setCursos(
            cursosData.map((c) => ({
              value: c.id_curso, 
              label: c.nombre_curso,
            }))
          );
        }
        setLoadingCursos(false); // Desactivar el loading
      };

      fetchCursos();
    }
  }, [selectedPeriodo]); // Solo se ejecuta cuando cambia el `selectedPeriodo`

  // Cargar las asignaturas cuando se selecciona un curso
  useEffect(() => {
    if (selectedCurso) {
      setLoadingAsignaturas(true); // Activar el loading de las asignaturas
      const fetchAsignaturas = async () => {
        const asignaturasData = await getAsignaturasPorCurso(selectedCurso);
        if (asignaturasData.success !== false) {
          setAsignaturas(
            asignaturasData.map((a) => ({
              value: a.id_asignatura,  // Usamos `id` de asignatura como value
              label: a.nombre_asignatura,  // Usamos `nombre` de asignatura como label
            }))
          );
        }
        setLoadingAsignaturas(false); // Desactivar el loading
      };

      fetchAsignaturas();
    }
  }, [selectedCurso]); // Solo se ejecuta cuando cambia el `selectedCurso`

  const handleUpload = (info) => {
    const files = info.fileList
      // Acepta PDFs (a veces file.type viene vacío en Safari, por eso también validamos la extensión)
      .filter(file => file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'))
      // Límite 2 MB con mensaje
      .filter(file => {
        const size = file.size ?? file.originFileObj?.size ?? 0;
        if (size > MAX_BYTES) {
          modal.warning({
            title: 'Upload',
            content: `${file.name} supera el límite de ${MAX_MB} MB`,
          });
          return false;
        }
        return true;
      })
      // Normaliza para tu estado
      .map(file => ({
        uid: file.uid,
        name: file.name,
        originFileObj: file.originFileObj || file, // por si viene directo
        url: file.originFileObj ? URL.createObjectURL(file.originFileObj) : undefined,  // Aquí está el uso correcto
      }));

    setArchivos(files);
  };


  useEffect(() => {
  if (selectedAsignatura && selectedCurso && selectedPeriodo) {
    const fetchRecursos = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          message.error('No se encontró el token de autenticación');
          return;
        }

        // URL del endpoint para consultar los recursos del usuario, curso, asignatura y periodo
        const response = await fetch(URL + `/recursos/?usuario_id=${localStorage.getItem('user_id')}&curso_id=${selectedCurso}&asignatura_id=${selectedAsignatura}&periodo_id=${selectedPeriodo}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data) {
          // Mapea los recursos y actualiza el estado
          const recursosConDetalles = data.map((archivo) => ({
            id: archivo.id,  // Asegúrate de tener un identificador único para cada recurso
            nombre: archivo.nombre_archivo,
            curso: cursos.find(c => c.value === selectedCurso)?.label || '',
            url: archivo.url,  // URL para la descarga o vista previa
          }));

          setRecursos(recursosConDetalles);
        }
      } catch (error) {
        console.error("Error al obtener los recursos:", error);
        message.error('Hubo un error al obtener los recursos');
      }
    };

    fetchRecursos();
  }
}, [selectedAsignatura, asignaturas, selectedCurso, cursos, selectedPeriodo, periodos]);  // Dependencias de la consulta

  const onFinish = async (values) => {

    setSubiendo(true);  // Empieza la carga

    const token = localStorage.getItem('access_token'); // Recuperamos el token desde el localStorage
    if (!token) {
      return { success: false, message: 'No se encontró el token de autenticación' };
    }
    // Verifica si al menos se ha seleccionado un archivo
    if (archivos.length === 0) {
      Modal.info({ title: 'Advertencia', content: 'Debes seleccionar al menos un archivo PDF antes de guardar' });
      setSubiendo(false);
      return;
    }

  const formData = new FormData();

  const usuarioId = localStorage.getItem('user_id');

  // Agrega cada archivo seleccionado al FormData
  archivos.forEach((archivo) => {
    formData.append('files', archivo.originFileObj);  // 'files' es el nombre del campo esperado en el backend
  });

  // Agrega los otros campos del formulario
  formData.append('periodo', selectedPeriodo);
  formData.append('curso', selectedCurso);
  formData.append('asignatura', selectedAsignatura);
  formData.append('usuario', usuarioId);

  try {
    // Realiza la solicitud POST al servidor para subir los archivos
    const response = await fetch(URL + '/subir-recurso/', {  // Asegúrate de usar la URL correcta de tu servidor
     method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,  // Enviamos el token en la cabecera de autorización
      },
      body: formData, // Enviamos los datos del usuario
    });

    const data = await response.json();

    if (data) {
      //console.log("respuesta: ", data);
      //message.success('📄 Recurso subido y guardado correctamente');
      // Agregar archivos al listado de recursos subidos
      const nuevosRecursos = data.map((archivo) => ({
        id: archivo.id,
        nombre: archivo.nombre_archivo,
        curso: cursos.find(c => c.value === selectedCurso)?.label || '',
        url: archivo.url, // esto ya lo tenías en handleUpload
      }));

      setRecursos(nuevosRecursos);

      setArchivos([]);  // Limpiar la lista de archivos después de la subida

      form.resetFields();  // Resetear el formulario

    } else {

    }
  } catch (error) {

  } finally {
    setSubiendo(false);  // Finaliza la carga
  }
};

// Función para manejar la actualización de vistas y descargas
const handleUpdateRecurso = async (id, action) => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      message.error('No se encontró el token de autenticación');
      return;
    }

    // Enviar la solicitud para actualizar el recurso
    const response = await fetch(URL + `/update-recurso/${id}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action }),  // Enviar la acción ('view' o 'download')
    });

    const data = await response.json();
    console.log(data);

  } catch (error) {
    console.error("Error al actualizar el recurso:", error);
    message.error('Hubo un error al procesar la solicitud');
  }
};

// Función para la vista previa del archivo
const handleVistaPrevia = (id, url) => {
  console.log(`[VISTA] id=${id}`);
  handleUpdateRecurso(id, 'view');  // Llamar a la función con la acción 'view'
  window.open(url);
};

// Función para manejar la descarga del archivo
const handleDescargar = (id, url) => {
  console.log(`[DESCARGAR] id=${id}`);
  handleUpdateRecurso(id, 'download');  // Llamar a la función con la acción 'download'
  window.open(url.replace("dl=0", "dl=1"), "_blank");  // Abrir la URL en una nueva pestaña para descargar el archivo
};

const columnas = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 240, 
      onCell: () => ({ style: { display: 'none' } }),
      onHeaderCell: () => ({ style: { display: 'none' } }), },// opcional, solo para ver el ID
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', width: 40},
    { title: 'Curso', dataIndex: 'curso', key: 'curso', width: 40 },
    {
      title: 'Acción',
      key: 'accion',
      render: (_, record) => (
        <>
           {/* Botón para vista previa */}
        <Button
          icon={<EyeOutlined />}  
          onClick={() => handleVistaPrevia(record.id, record.url)}  // Aquí llamamos la función con 'view'
          style={{ marginRight: 8 }}
        >
          Ver
        </Button>
        
        {/* Botón para descarga */}
        <Button
          icon={<DownloadOutlined />}
          onClick={() => handleDescargar(record.id, record.url)}  // Aquí llamamos la función con 'download'
          style={{ marginRight: 8 }}
        >
          Descargar
        </Button>
        </>
      ), width: 0
    },
  ];

  return (
    <Card style={{ maxWidth: 900, margin: 'auto' }}>
      {modalContextHolder}
      <Title level={4}>Subir Recurso Pedagógico</Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ marginBottom: 32 }}
      >
        <Form.Item
          label={<span style={labelStyle}>Período Lectivo</span>}
          name="periodo"
          rules={[{ required: true, message: "Seleccione un período" }]}
        >
          <Select
            placeholder="Seleccione período"
            style={inputStyle}
            options={periodos}
            loading={loading}
            onChange={(value) => setSelectedPeriodo(value)}
            disabled={loading} // Deshabilitar el Select mientras cargamos los datos
          />
        </Form.Item>

        {selectedPeriodo && (
          <Form.Item
            label={<span style={labelStyle}>Curso</span>}
            name="curso"
            rules={[{ required: true, message: "Seleccione un curso" }]}
          >
            <Select
              placeholder="Seleccione curso"
              style={inputStyle}
              options={cursos}
              loading={loadingCursos}  // Muestra el spinner de carga mientras se cargan los cursos
              onChange={(value) => setSelectedCurso(value)}
              disabled={loadingCursos}  // Deshabilita el Select si está cargando
            />
          </Form.Item>
        )}

        {selectedCurso && (
          <Form.Item
            label={<span style={labelStyle}>Asignatura</span>}
            name="asignatura"
            rules={[{ required: true, message: "Seleccione una asignatura" }]}
          >
            <Select
              placeholder="Seleccione asignatura"
              style={inputStyle}
              options={asignaturas}
              loading={loadingAsignaturas}
              onChange={(value) => setSelectedAsignatura(value)}
              disabled={loadingAsignaturas}  // Deshabilita el Select mientras se cargan las asignaturas
            />
          </Form.Item>
        )}

        <Form.Item label={<span style={labelStyle}>Archivo PDF Recurso Pedagógico</span>}>
          <Dragger
            accept=".pdf"
            beforeUpload={() => false}  // Evitar que el archivo se suba automáticamente al seleccionarlo
            showUploadList={true}  // Mostrar lista de archivos seleccionados
            fileList={archivos.map((file) => ({
              uid: file.uid,
              name: file.name,
              status: 'done',  // El archivo ya está "hecho" ya que está en la lista
            }))}
            onChange={handleUpload}  // Llamamos a handleUpload para agregar el archivo
            multiple={true}  // Permitir la carga de varios archivos
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Haz clic o arrastra varios archivos PDF aquí</p>
            <p className="ant-upload-hint">Solo se aceptan archivos PDF</p>
          </Dragger>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            icon={<UploadOutlined />}
            loading={loading}  // Muestra el spinner mientras loading sea true
          >
            Subir Recurso (máx. {MAX_MB} MB)
          </Button>

          {subiendo && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" /> {/* Este es el Spinner de Ant Design */}
              <p>Subiendo archivo...</p>
            </div>
          )}
        </Form.Item>
      </Form>

      <Title level={5}>Recursos Subidos</Title>
      <Table rowKey="id" columns={columnas} dataSource={recursos} pagination={{ pageSize: 5 }} style={{ width:'100%'}} />

      <Modal
        open={previewVisible}
        title="Vista Previa del Recurso"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={1000}
        style={{ top: 20 }}
      >
      </Modal>
    </Card>
  );
};

export default SubirRecurso;
